/**
 * PendingGuestFeesCard — group-level rollup of ALL outstanding guest fees
 * across sessions, shown on the Money tab so guest money isn't forgotten.
 * One-tap Collect / Waive using the existing per-session guest-fee endpoints.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, UserRound, XCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

export default function PendingGuestFeesCard({ groupId, isAdmin, onChange }) {
  const [data, setData] = useState(null);
  const [collectFor, setCollectFor] = useState(null);
  const [waiveFor, setWaiveFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${groupId}/pending-guest-fees`);
      setData(r.data);
    } catch (e) {
      setData(null);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.items || data.items.length === 0) return null;

  const refresh = () => { load(); onChange?.(); };

  return (
    <div
      className="rounded-2xl border-2 border-violet-500/30 bg-gradient-to-br from-violet-500/5 via-background to-background p-4"
      data-testid="pending-guest-fees-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserRound className="w-4 h-4 text-violet-600 dark:text-violet-300" />
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em]">Guest fees due</h3>
        </div>
        <span className="text-xs font-bold text-violet-700 dark:text-violet-300 tabular-nums" data-testid="pending-guest-fees-total">
          ₹{Math.round(data.total_pending).toLocaleString()} · {data.count} {data.count === 1 ? 'guest' : 'guests'}
        </span>
      </div>

      <div className="mt-2 divide-y divide-border/60">
        {data.items.map((it) => (
          <div key={`${it.session_id}-${it.guest_id}`} className="py-2 flex items-center gap-2 text-sm" data-testid={`pending-guest-fee-${it.guest_id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">
                {it.guest_name}
                <span className="ml-1.5 font-bold text-violet-700 dark:text-violet-300 tabular-nums">₹{Math.round(it.amount)}</span>
              </p>
              <Link
                to={`/groups/${groupId}/sessions/${it.session_id}`}
                className="text-xs text-muted-foreground hover:text-primary truncate inline-flex items-center gap-1"
                data-testid={`pending-guest-fee-session-link-${it.guest_id}`}
              >
                {it.session_title || 'Session'}{it.session_date ? ` · ${it.session_date}` : ''}
                <ExternalLink className="w-2.5 h-2.5 shrink-0" />
              </Link>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  className="h-7 px-2.5 text-xs rounded-full"
                  onClick={() => setCollectFor(it)}
                  data-testid={`pending-guest-fee-collect-${it.guest_id}`}
                >
                  Collect
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs rounded-full text-muted-foreground"
                  onClick={() => setWaiveFor(it)}
                  data-testid={`pending-guest-fee-waive-${it.guest_id}`}
                >
                  Waive
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <CollectDialog item={collectFor} onClose={() => setCollectFor(null)} onSuccess={refresh} />
      <WaiveDialog item={waiveFor} onClose={() => setWaiveFor(null)} onSuccess={refresh} />
    </div>
  );
}

function CollectDialog({ item, onClose, onSuccess }) {
  const [cash, setCash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (item) setCash(String(item.amount || '')); }, [item]);

  const onConfirm = async () => {
    const amt = parseFloat(cash);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      await api.post(`/sessions/${item.session_id}/guest-fee/${item.guest_id}/collect`, { cash_amount: amt });
      toast.success(`Collected ₹${Math.round(amt)} from ${item.guest_name}`);
      onClose();
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Collect failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pending-guest-fee-collect-dialog">
        <DialogHeader>
          <DialogTitle>Collect from {item?.guest_name}</DialogTitle>
          <DialogDescription>
            {item?.session_title}{item?.session_date ? ` · ${item.session_date}` : ''}. Cash goes into the group fund.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor="pgf-cash" className="text-xs font-semibold uppercase tracking-wider">Cash amount (₹)</Label>
          <Input
            id="pgf-cash"
            type="number"
            inputMode="decimal"
            min="0"
            value={cash}
            onChange={(e) => setCash(e.target.value)}
            data-testid="pending-guest-fee-cash-input"
            className="mt-1 text-base"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="pending-guest-fee-collect-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={submitting} data-testid="pending-guest-fee-collect-confirm" className="gap-1.5">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {submitting ? 'Collecting…' : 'Mark collected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WaiveDialog({ item, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (item) setReason(''); }, [item]);

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/sessions/${item.session_id}/guest-fee/${item.guest_id}/waive`, { reason: reason || undefined });
      toast.success('Guest fee waived');
      onClose();
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Waive failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pending-guest-fee-waive-dialog">
        <DialogHeader>
          <DialogTitle>Waive {item?.guest_name}&apos;s fee</DialogTitle>
          <DialogDescription>Comp the guest. An audit row is kept with your note.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor="pgf-waive-reason" className="text-xs font-semibold uppercase tracking-wider">Reason (optional)</Label>
          <Textarea
            id="pgf-waive-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="pending-guest-fee-waive-reason"
            className="mt-1 text-sm"
            placeholder="e.g. First-time visit"
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="pending-guest-fee-waive-cancel">Cancel</Button>
          <Button onClick={onConfirm} variant="destructive" disabled={submitting} data-testid="pending-guest-fee-waive-confirm" className="gap-1.5">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            Waive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
